import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EmailLoginForm } from '../components/auth/EmailLoginForm';
import { OTPVerificationForm } from '../components/auth/OTPVerificationForm';
import { useOTPAuth } from '../hooks/useOTPAuth';

export const LoginPage: React.FC = () => {
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [logoVariant, setLogoVariant] = useState<1 | 2 | 3>(1);
  const { requestOTP, verifyOTP, createAnonymousSession } = useOTPAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get redirect parameter from URL
  const redirectPath = searchParams.get('redirect') || '/chat';

  const handleEmailSubmit = (submittedEmail: string) => {
    setEmail(submittedEmail);
    setStep('otp');
  };

  const handleOTPSuccess = () => {
    // Redirect to the intended destination or default to /chat
    navigate(redirectPath);
  };

  const handleAnonymousContinue = async () => {
    const result = await createAnonymousSession();
    if (result.success) {
      // For anonymous sessions, always go to /chat (don't redirect to protected routes)
      navigate('/chat');
    } else {
      console.error('Failed to create anonymous session:', result.error);
    }
  };

  const handleBack = () => {
    setStep('email');
  };

  const handleLogoClick = () => {
    setLogoVariant((prev) => (prev === 3 ? 1 : (prev + 1) as 1 | 2 | 3));
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-neutral-light-gray via-neutral-white to-brand-teal/10 px-4 py-8">
      <div className="w-full max-w-md bg-neutral-white dark:bg-neutral-black rounded-2xl shadow-xl p-10 border border-neutral-light-gray dark:border-neutral-dark-gray">
        <div className="flex justify-center mb-10">
          <img
            src={`/images/logos/logo-color-tabline${logoVariant}.svg`}
            alt="ChatHTL - AI-Powered Hotel Intelligence"
            className="h-32 cursor-pointer"
            onClick={handleLogoClick}
          />
        </div>

        {step === 'email' ? (
          <>
            <EmailLoginForm
              onSuccess={handleEmailSubmit}
              requestOTP={requestOTP}
            />
            <div className="mt-6 text-center">
              <button
                onClick={handleAnonymousContinue}
                className="text-brand-teal hover:text-brand-teal-hover hover:underline font-medium transition-colors text-body"
              >
                Continue as guest →
              </button>
            </div>
          </>
        ) : (
          <OTPVerificationForm
            email={email}
            onSuccess={handleOTPSuccess}
            onBack={handleBack}
            verifyOTP={verifyOTP}
            onResend={() => requestOTP(email)}
          />
        )}
      </div>

      {/* Policies Link */}
      <div className="mt-6 text-center">
        <button
          onClick={() => navigate('/policies')}
          className="text-sm text-neutral-medium-gray hover:text-brand-teal transition-colors"
        >
          Terms & Policies
        </button>
      </div>
    </div>
  );
};
